import i18n from '@/i18n.js';

/**
 * Extracted loading animation logic from LcForm.
 * All state is passed in reactively from the component.
 *
 * @param {Object} deps
 * @param {import('vue').ComputedRef} deps.applicationSettings - writable computed ref
 * @param {import('vue').Ref} deps.app - ref
 * @param {import('vue').Ref} deps.activeStep - ref
 * @param {import('vue').Ref} deps.rotatingMessagesInterval - ref
 * @param {import('vue').Ref} deps.loadingAnimationInterval - ref
 */
export function useLoadingAnimation(deps) {

    function getLoadingScreenConfig() {
        let step = deps.activeStep.value;
        if (!step) {
            return [];
        }

        // new loadingScreen property takes priority
        if (step.hasOwnProperty('loadingScreen')) {
            let config = step.loadingScreen;

            // function: call with app state
            if (typeof config === 'function') {
                config = config(deps.app.value);
            }

            // falsy: no loading screen
            if (!config) {
                return [];
            }

            // string shorthand: 3 seconds with message
            if (typeof config === 'string') {
                return [{ seconds: 3, message: config }];
            }

            // single object: wrap in array
            if (typeof config === 'object' && !Array.isArray(config)) {
                return [config];
            }

            // array: use as-is
            if (Array.isArray(config)) {
                return config;
            }

            return [];
        }

        // fallback to legacy loadingFunction
        if (step.loadingFunction && typeof step.loadingFunction === 'function') {
            let result = step.loadingFunction(deps.app.value);
            if (result && Array.isArray(result) && result.length > 0) {
                return result;
            }
        }

        return [];
    }

    async function resolveLoadingAnimation() {
        let screens = getLoadingScreenConfig();
        const step = deps.activeStep.value;

        for (const animation of screens) {
            // The applicant left this step mid-sequence: don't start its next screen on
            // top of the step they are on now.
            if (deps.activeStep.value !== step) {
                return;
            }
            let animationTimeInSeconds = animation.seconds ?? 3;
            let animationMessage = animation.message ?? null;
            let options = {
                image: animation.image ?? null,
                animationType: animation.animationType ?? 'spinner',
                showBrandLogo: animation.showBrandLogo ?? null,
            };
            if (animationTimeInSeconds) {
                await doLoadingAnimation(animationTimeInSeconds, animationMessage, options);
            }
        }
    }

    // Which doLoadingAnimation() call started last (see the overlap note inside it).
    let latestAnimation = 0;

    async function doLoadingAnimation(animationTime = null, message = null, options = {}) {
        if (!animationTime || isNaN(animationTime) || animationTime <= 0) {
            return;
        }

        // convert milliseconds to seconds, just in case
        if (animationTime > 999) {
            animationTime = Math.round(animationTime / 1000);
        }

        let appSettings = deps.applicationSettings;

        // store previous logo state for restoration
        let previousShowLogo = appSettings.showBrandLogoDuringAnimation;

        appSettings.loadingAnimationMessage = message;
        appSettings.loadingAnimationImage = options.image ?? null;
        appSettings.loadingAnimationType = options.animationType ?? 'spinner';
        if (options.showBrandLogo != null) {
            appSettings.showBrandLogoDuringAnimation = options.showBrandLogo;
        }
        appSettings.loadingAnimationSeconds = -1;

        // Each animation stops its OWN timer. Clearing "whatever is in the shared ref"
        // broke as soon as two animations overlapped (a step change during the previous
        // step's animation): the first timer was orphaned, never stopped, and from then on
        // cleared every later animation's timer each second, so every later animation --
        // and every initStep and pre-submit wait behind one -- hung for good.
        let intervalId = null;
        // The newest animation owns the shared ref and the screen; an older one finishing
        // must not clear the ref or reset the screen the newer one is still showing.
        const thisAnimation = ++latestAnimation;
        const isNewest = () => thisAnimation === latestAnimation;
        const stopOwnTimer = () => {
            clearInterval(intervalId);
            if (isNewest()) {
                deps.loadingAnimationInterval.value = null;
            }
        };

        try {
            let finishedAsNewest = false;
            await new Promise((resolve) => {
                intervalId = setInterval(() => {
                    animationTime--;
                    if (animationTime <= 0) {
                        // Read ownership before stopOwnTimer releases the ref.
                        finishedAsNewest = isNewest();
                        stopOwnTimer();
                        resolve();
                    }
                }, 1000);
                deps.loadingAnimationInterval.value = intervalId;
            });

            if (finishedAsNewest) {
                // stop animation
                appSettings.loadingAnimationSeconds = 0;
                appSettings.loadingAnimationMessage = null;
                appSettings.loadingAnimationImage = null;
                appSettings.loadingAnimationType = 'spinner';
                appSettings.showBrandLogoDuringAnimation = previousShowLogo;
            }

        } catch (error) {
            stopOwnTimer();
        }
    }

    function startRotatingMessages(messages = null, defaultInterval = null) {
        let appSettings = deps.applicationSettings;
        let screens = messages ?? appSettings.submissionLoadingScreens;
        defaultInterval = defaultInterval ?? appSettings.submissionLoadingScreenInterval ?? 4;
        if (!screens || screens.length === 0) {
            return;
        }

        let index = 0;

        // show the first message immediately
        applyLoadingScreen(screens[index]);

        const scheduleNext = () => {
            let currentDuration = screens[index].seconds ?? defaultInterval;
            deps.rotatingMessagesInterval.value = setTimeout(() => {
                index = (index + 1) % screens.length;
                applyLoadingScreen(screens[index]);
                scheduleNext();
            }, currentDuration * 1000);
        };

        scheduleNext();
    }

    function stopRotatingMessages() {
        let appSettings = deps.applicationSettings;
        let interval = deps.rotatingMessagesInterval.value;
        if (interval) {
            clearTimeout(interval);
            deps.rotatingMessagesInterval.value = null;
        }
        appSettings.loadingAnimationMessage = null;
        appSettings.loadingAnimationImage = null;
        appSettings.loadingAnimationType = 'spinner';
    }

    function applyLoadingScreen(screen) {
        let appSettings = deps.applicationSettings;
        if (screen.translationKey) {
            let text = i18n.global.t(screen.translationKey);
            appSettings.loadingAnimationMessage = '<h3 class="my-4 text-center h4 text-muted">' + text + '</h3>';
        } else {
            appSettings.loadingAnimationMessage = screen.message ?? null;
        }
        appSettings.loadingAnimationImage = screen.image ?? null;
        appSettings.loadingAnimationType = screen.animationType ?? 'spinner';
        if (screen.showBrandLogo != null) {
            appSettings.showBrandLogoDuringAnimation = screen.showBrandLogo;
        }
    }

    function cleanup() {
        if (deps.loadingAnimationInterval.value) {
            clearInterval(deps.loadingAnimationInterval.value);
            deps.loadingAnimationInterval.value = null;
        }
        stopRotatingMessages();
    }

    return {
        getLoadingScreenConfig,
        resolveLoadingAnimation,
        doLoadingAnimation,
        startRotatingMessages,
        stopRotatingMessages,
        applyLoadingScreen,
        cleanup,
    };
}
